import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, BookOpen, Heart } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const JOTFORM_URL = "https://form.jotform.com/250741911322347";

const BENEFITS = [
  { icon: BookOpen, title: "Contenuti pratici", body: "Non teorie astratte: gesti concreti che puoi applicare oggi stesso nella tua giornata con il bambino." },
  { icon: Heart, title: "Sguardo rispettoso", body: "Un approccio che rispetta i tempi e i bisogni del bambino, coerente con il Metodo T.A.T.A.®." },
  { icon: Download, title: "Scaricabile subito", body: "Riceverai la guida direttamente via email, in formato PDF, pronta da leggere e conservare." },
];

export default function Guida() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  useEffect(() => {
    // load jotform embed handler for auto-resize
    const s = document.createElement("script");
    s.src = "https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js";
    s.async = true;
    document.body.appendChild(s);
    return () => { s.remove(); };
  }, []);

  return (
    <div data-testid="guida-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[70vh] pt-32 md:pt-40 pb-12 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Guida gratuita in regalo</p>
          <h1 className="font-display text-[14vw] md:text-[11vw] lg:text-[14rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Un piccolo", <span key="1" className="text-[#FF2768]">dono</span>, "per te."]} stagger={0.14} />
          </h1>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              Una guida gratuita, curata con la stessa attenzione che riservo a ogni bambino e a ogni famiglia. Scaricala subito compilando il form qui sotto.
            </p>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-52 w-32 md:w-52 rounded-full opacity-80"
        />
      </section>

      {/* BENEFITS */}
      <section className="px-6 md:px-10 py-16 bg-[#81C3C9]/25" data-testid="benefits-section">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="p-8 rounded-3xl bg-white"
                data-testid={`benefit-${i}`}
              >
                <span className="h-12 w-12 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-5">
                  <Icon size={20} />
                </span>
                <h3 className="font-display text-3xl text-[#3D2140] mb-3">{b.title}</h3>
                <p className="text-[#3D2140]/75 leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* JOTFORM EMBED */}
      <section className="px-6 md:px-10 py-16 md:py-24 max-w-[1200px] mx-auto" data-testid="jotform-section">
        <div className="mb-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-3">— Compila e ricevi</p>
          <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
            Scarica la <span className="text-[#FF2768]">guida gratuita.</span>
          </h2>
        </div>
        <div className="rounded-[24px] overflow-hidden border border-[#81C3C9]/40 bg-white shadow-lg">
          <iframe
            src={JOTFORM_URL}
            title="Guida gratuita Teresa Vilardo"
            width="100%"
            height="900"
            frameBorder="0"
            scrolling="auto"
            allowFullScreen
            data-testid="jotform-iframe"
            style={{ display: "block", minHeight: "700px" }}
          />
        </div>
        <div className="mt-6 text-center">
          <a href={JOTFORM_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#3D2140]/60 hover:text-[#FF2768] link-underline" data-testid="jotform-open-external">
            Apri il form in una nuova finestra →
          </a>
        </div>
      </section>

      {/* CTA CHIUSURA */}
      <section className="px-6 md:px-10 py-16 md:py-24" data-testid="guida-closing">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-editorial text-2xl md:text-3xl text-[#3D2140] italic leading-snug max-w-3xl mx-auto">
            "Ogni bambino è unico. Ogni famiglia è una storia. Ogni guida è un punto di partenza."
          </p>
          <p className="mt-6 text-sm uppercase tracking-[0.4em] text-[#81C3C9]">— Teresa Vilardo</p>
          <div className="mt-12">
            <Link to="/prenota" data-testid="guida-cta-booking" className="btn-tv-primary">
              Vuoi una consulenza personalizzata? <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
